// The wiki's pages, persisted per tenant.
//
// localStorage for the same reason the runtime-lens store starts there: a wiki
// you can generate, read, and regenerate in one sitting proves the surface
// without waiting on a schema, and plain versioned JSON makes a later move to
// the backend a transport change. Unlike a lens spec, a wiki page's identity
// must SURVIVE regeneration — the maintenance story is "the same page gets
// updated", not "a new page pile appears" — so applying a generation is a
// matching problem, not an overwrite (see matchGenerationToPages).

import { browserStorageKey, discardLegacyTenantEntry } from "./browserStorageScope.js";

const STORAGE_KEY_PREFIX = "origin.xray.wiki.v2";
const LEGACY_STORAGE_KEY_PREFIX = "origin.xray.wiki.v1";

// Session-overlap threshold for matching a regenerated page to a stored one
// whose slug drifted. Over the smaller set, so a page that grew or shrank its
// evidence still matches when what remains points at the same work.
const MATCH_OVERLAP = 0.5;

// Growth bounds. Pages and logs otherwise grow monotonically across every
// scope a tenant ever regenerates, and the failure mode of an unbounded
// bucket is the worst one this store has: localStorage writes start throwing
// and every later run finishes but persists nothing.
export const MAX_WIKI_PAGES = 60;
export const LOG_KEEP = 20;

let _storageOverride = null;
function storage() {
  if (_storageOverride) return _storageOverride;
  return typeof localStorage === "undefined" ? null : localStorage;
}

/** Test-only: supply a storage backend. Pass null to fall back to the global. */
export function _setStorageForTests(s) { _storageOverride = s; }

function storageKey(tenantId) {
  discardLegacyTenantEntry(storage(), LEGACY_STORAGE_KEY_PREFIX, tenantId);
  return browserStorageKey(STORAGE_KEY_PREFIX, tenantId);
}

const listeners = new Set();
function notify() {
  for (const fn of listeners) {
    try { fn(); } catch { /* one listener's failure must not block its siblings */ }
  }
}

/** Subscribe to wiki changes. Returns an unsubscribe. */
export function subscribeWiki(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

const EMPTY = { pages: [], runs: {} };

/**
 * The tenant's wiki: every page across every scope, plus the latest run per
 * scopeKey (`runs`). Each run record carries the profile its scout wrote —
 * an org profile shown over an endpoint's pages (or the reverse) misattributes
 * both, which is why there is no store-wide profile anymore.
 *
 * Never throws; a corrupt bucket reads as an empty wiki. The pre-partition
 * shape ({profile, lastRun}) migrates on read: its single run files under the
 * scopeKey it recorded.
 */
export function readWiki(tenantId) {
  try {
    const key = storageKey(tenantId);
    if (!key) return EMPTY;
    const raw = storage()?.getItem(key);
    if (!raw) return EMPTY;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || !Array.isArray(parsed.pages)) return EMPTY;
    const pages = parsed.pages.filter((p) => p && typeof p.slug === "string" && typeof p.markdown === "string");
    let runs = parsed.runs && typeof parsed.runs === "object" ? parsed.runs : {};
    if (!parsed.runs && parsed.lastRun) {
      const key = parsed.lastRun.scopeKey || "org";
      runs = { [key]: { ...parsed.lastRun, profile: String(parsed.profile || "") } };
    }
    return { pages, runs };
  } catch {
    return EMPTY;
  }
}

function writeWiki(tenantId, wiki) {
  try {
    const key = storageKey(tenantId);
    if (!key) return { ok: false, problems: ["verified tenant and user identity are required"] };
    storage()?.setItem(key, JSON.stringify(wiki));
  } catch (err) {
    return { ok: false, problems: [`could not persist: ${err?.message || String(err)}`] };
  }
  notify();
  return { ok: true, problems: [] };
}

function sessionOverlap(a, b) {
  const as = new Set(a || []);
  const bs = new Set(b || []);
  if (!as.size || !bs.size) return 0;
  let hit = 0;
  for (const id of as) if (bs.has(id)) hit += 1;
  return hit / Math.min(as.size, bs.size);
}

// Title identity is deliberately narrow: case and whitespace may drift across
// model runs, while punctuation remains meaningful. Only unique keys are safe
// fallback identities; a repeated generic title must not merge distinct pages.
function normalizedTitle(title) {
  return String(title || "").trim().toLowerCase().replace(/\s+/g, " ");
}

const VALID_WIKI_PAGE_KINDS = new Set(["workflow", "subsystem", "tool", "practice"]);
const MAX_CLUSTER_ID = 2147483647;
const GENERATION_UUID = /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i;

function normalizedKind(kind) {
  const key = String(kind || "").trim().toLowerCase();
  return VALID_WIKI_PAGE_KINDS.has(key) ? key : null;
}

function normalizedClusterSource(page) {
  const raw = String(page?.sourceClusterId ?? "").trim();
  if (!/^\d+$/.test(raw)) return null;
  const value = Number(raw);
  const generationId = String(page?.sourceClusterGenerationId ?? "").trim().toLowerCase();
  if (!Number.isInteger(value) || value < 0 || value > MAX_CLUSTER_ID || !GENERATION_UUID.test(generationId)) return null;
  return `${generationId}:${value}`;
}

function uniquePagesByTitle(pages) {
  const unique = new Map();
  const ambiguous = new Set();
  for (const page of pages) {
    const key = normalizedTitle(page.title);
    if (!key || ambiguous.has(key)) continue;
    if (unique.has(key)) {
      unique.delete(key);
      ambiguous.add(key);
    } else {
      unique.set(key, page);
    }
  }
  return unique;
}

/**
 * Match a generation's pages to the stored ones. Pure, so the identity rules
 * are testable without storage.
 *
 * Identity is PARTITIONED BY SCOPE: only pages generated under this run's
 * scopeKey participate. An endpoint's sessions are a subset of the org's, so
 * cross-scope overlap matching let a narrow run capture — and overwrite — a
 * broad scope's page with narrower evidence; each scope keeps its own wiki
 * instead, and a run says nothing about any other scope's pages.
 *
 * Within the scope: reserve every exact slug first, so a fallback for an
 * earlier incoming page cannot steal a later page's durable identity. Then
 * match a normalized exact title only when it is unique on both sides and the
 * pages have the same valid kind and equal structured cluster + generation
 * identity. Last, use evidence-session overlap for renamed pages. The
 * overlap pass is greedy in generation order; each stored page matches at
 * most once.
 *
 * @returns {{updates: [{incoming, existing}], creates: [incoming],
 *            staled: [existing]}}
 *   `staled` = this scope's stored pages the run did not re-produce.
 */
export function matchGenerationToPages(existingPages, generation) {
  const scopeKey = generation.meta?.scopeKey || null;
  const scoped = existingPages.filter((p) => (p.scopeKey || null) === scopeKey);
  const incomingPages = generation.pages;
  const taken = new Set();
  const matches = new Array(incomingPages.length).fill(null);
  const updates = [];
  const creates = [];
  const bySlug = new Map(scoped.map((p) => [p.slug, p]));

  // Exact durable identities are authoritative across the whole generation.
  for (let i = 0; i < incomingPages.length; i++) {
    const incoming = incomingPages[i];
    const direct = bySlug.get(incoming.slug);
    if (direct && !taken.has(direct.slug)) {
      taken.add(direct.slug);
      matches[i] = direct;
    }
  }

  const storedByTitle = uniquePagesByTitle(scoped);
  const incomingByTitle = uniquePagesByTitle(incomingPages);
  for (let i = 0; i < incomingPages.length; i++) {
    if (matches[i]) continue;
    const incoming = incomingPages[i];
    const titleKey = normalizedTitle(incoming.title);
    if (incomingByTitle.get(titleKey) !== incoming) continue;
    const sameTitle = storedByTitle.get(titleKey);
    if (!sameTitle || taken.has(sameTitle.slug)) continue;
    const kindKey = normalizedKind(incoming.kind);
    if (!kindKey || kindKey !== normalizedKind(sameTitle.kind)) continue;
    const clusterSource = normalizedClusterSource(incoming);
    if (clusterSource === null || clusterSource !== normalizedClusterSource(sameTitle)) continue;
    taken.add(sameTitle.slug);
    matches[i] = sameTitle;
  }

  for (let i = 0; i < incomingPages.length; i++) {
    if (matches[i]) continue;
    const incoming = incomingPages[i];
    let best = null;
    let bestScore = 0;
    for (const p of scoped) {
      if (taken.has(p.slug)) continue;
      const score = sessionOverlap(incoming.sessionIds, p.sessionIds);
      if (score > bestScore) { best = p; bestScore = score; }
    }
    if (best && bestScore >= MATCH_OVERLAP) {
      taken.add(best.slug);
      matches[i] = best;
    }
  }

  for (let i = 0; i < incomingPages.length; i++) {
    const incoming = incomingPages[i];
    if (matches[i]) updates.push({ incoming, existing: matches[i] });
    else creates.push(incoming);
  }
  const staled = scoped.filter((p) => !taken.has(p.slug) && !p.stale);
  return { updates, creates, staled };
}

// Slugs are unique only WITHIN a scope's partition, so anything keyed across
// the whole store keys on both.
const pageKey = (scopeKey, slug) => `${scopeKey || ""}::${slug}`;

/**
 * Fold one generation into the stored wiki. Updated pages keep their slug and
 * their log (identity is the point); staled pages are marked, never deleted —
 * a page this run didn't re-find may still describe real, older work, and the
 * reader decides what a stale page is worth.
 */
export function applyGeneration(tenantId, generation) {
  const now = generation.meta?.at || Date.now();
  const scopeKey = generation.meta?.scopeKey || null;
  const runNote = generation.meta?.rangeText
    ? `${generation.meta?.scopeText || "scope"} · ${generation.meta.rangeText}`
    : generation.meta?.scopeText || null;
  const wiki = readWiki(tenantId);
  const { updates, creates, staled } = matchGenerationToPages(wiki.pages, generation);

  const updatedByKey = new Map();
  for (const { incoming, existing } of updates) {
    updatedByKey.set(pageKey(existing.scopeKey, existing.slug), {
      ...existing,
      title: incoming.title,
      kind: incoming.kind,
      summary: incoming.summary,
      basis: incoming.basis,
      sourceClusterId: incoming.sourceClusterId ?? null,
      sourceClusterGenerationId: incoming.sourceClusterGenerationId ?? null,
      markdown: incoming.markdown,
      links: incoming.links,
      sessionIds: incoming.sessionIds,
      evidence: incoming.evidence,
      scopeKey,
      scopeText: generation.meta?.scopeText || existing.scopeText || null,
      stale: false,
      updatedAt: now,
      log: [...(existing.log || []), { at: now, action: "updated", note: runNote }],
    });
  }
  const staledKeys = new Set(staled.map((p) => pageKey(p.scopeKey, p.slug)));

  let pages = wiki.pages.map((p) => {
    const k = pageKey(p.scopeKey, p.slug);
    if (updatedByKey.has(k)) return updatedByKey.get(k);
    if (staledKeys.has(k)) {
      return { ...p, stale: true, log: [...(p.log || []), { at: now, action: "staled", note: "not re-found by the latest run over its scope" }] };
    }
    return p;
  });
  const touched = new Set(updatedByKey.keys());
  for (const incoming of creates) {
    let slug = incoming.slug;
    while (pages.some((p) => (p.scopeKey || null) === scopeKey && p.slug === slug)) slug = `${slug}-2`;
    touched.add(pageKey(scopeKey, slug));
    pages.push({
      ...incoming,
      slug,
      scopeKey,
      scopeText: generation.meta?.scopeText || null,
      stale: false,
      createdAt: now,
      updatedAt: now,
      log: [{ at: now, action: "created", note: runNote }],
    });
  }

  pages = pages.map((p) => ((p.log?.length || 0) > LOG_KEEP ? { ...p, log: p.log.slice(-LOG_KEEP) } : p));

  // Evict past the page cap: never a page this run just wrote, stale pages
  // first, oldest-updated first within each class. Evictions are reported in
  // the run's problems — a page silently gone reads as data loss.
  const problems = [...(generation.problems || [])];
  if (pages.length > MAX_WIKI_PAGES) {
    const evictable = pages
      .filter((p) => !touched.has(pageKey(p.scopeKey, p.slug)))
      .sort((a, b) => (Number(Boolean(b.stale)) - Number(Boolean(a.stale))) || ((a.updatedAt || 0) - (b.updatedAt || 0)));
    const evict = new Set(evictable.slice(0, pages.length - MAX_WIKI_PAGES).map((p) => pageKey(p.scopeKey, p.slug)));
    if (evict.size) {
      pages = pages.filter((p) => !evict.has(pageKey(p.scopeKey, p.slug)));
      problems.push(`store: evicted ${evict.size} page${evict.size === 1 ? "" : "s"} (oldest, stale first) to stay under the ${MAX_WIKI_PAGES}-page cap`);
    }
  }

  const run = {
    at: now,
    scopeKey,
    scopeText: generation.meta?.scopeText || null,
    rangeText: generation.meta?.rangeText || null,
    model: generation.meta?.model || null,
    profile: generation.profile || "",
    problems,
    counts: { updated: updates.length, created: creates.length, staled: staled.length },
  };
  // Runs live only as long as their scope has pages (plus the run just
  // written): `pages` is capped, so this bounds `runs` off the same cap
  // instead of letting a parallel map accumulate one record per scope ever
  // generated. A scope whose pages all evicted reads as "no wiki for this
  // scope yet" again, which is the truthful state.
  const scopesWithPages = new Set(pages.map((p) => p.scopeKey || "org"));
  scopesWithPages.add(scopeKey || "org");
  const runs = {};
  for (const [k, v] of Object.entries({ ...wiki.runs, [scopeKey || "org"]: run })) {
    if (scopesWithPages.has(k)) runs[k] = v;
  }

  const next = { pages, runs };
  const w = writeWiki(tenantId, next);
  return { ...w, counts: run.counts };
}

/** Remove one page outright, identified by its scope and slug. Returns whether it was there. */
export function deleteWikiPage(tenantId, scopeKey, slug) {
  const wiki = readWiki(tenantId);
  const kept = wiki.pages.filter((p) => !((p.scopeKey || null) === (scopeKey || null) && p.slug === slug));
  if (kept.length === wiki.pages.length) return false;
  return writeWiki(tenantId, { ...wiki, pages: kept }).ok;
}

/** Test-only: drop a tenant's bucket. */
export function _resetForTests(tenantId) {
  try { const key = storageKey(tenantId); if (key) storage()?.removeItem(key); } catch { /* noop */ }
  listeners.clear();
}
